'use client';

import { createContext, useContext, useMemo, useReducer, type Dispatch, type ReactNode } from 'react';
import {
    buildSplitWizardInitialState, type SplitWizardData, type SplitWizardState, type SplitWizardStep,
} from './split-wizard-state';
import { createSplitWizardReducer, type SplitWizardAction } from './split-wizard-reducer';


type SplitWizardContextValue = {
    state: SplitWizardState;
    dispatch: Dispatch<SplitWizardAction>;
    stepOrder: SplitWizardStep[];
};


const SplitWizardContext = createContext<SplitWizardContextValue | null>(null);


type SplitWizardProviderProps = {
    children: ReactNode;
    initialStep: SplitWizardStep;
    initialData?: SplitWizardData;
    stepOrder: SplitWizardStep[];
};


export const SplitWizardProvider = ({
    children,
    initialStep,
    initialData,
    stepOrder,
}: SplitWizardProviderProps) => {
    const reducer = useMemo(() => createSplitWizardReducer(stepOrder), [ stepOrder ]);

    const [ state, dispatch ] = useReducer(
        reducer,
        undefined,
        () => buildSplitWizardInitialState(initialStep, initialData),
    );

    const value = useMemo(
        () => ({ state, dispatch, stepOrder }),
        [ state, stepOrder ],
    );

    return (
        <SplitWizardContext.Provider value={ value }>
            { children }
        </SplitWizardContext.Provider>
    );
};


export const useSplitWizard = () => {
    const ctx = useContext(SplitWizardContext);
    if (!ctx) throw new Error('useSplitWizard must be used inside <SplitWizardProvider>');
    return ctx;
};
